import { useContext, useState } from "react";

import { Container } from "@mui/material";

import {
  ActionIcon,
  Autocomplete,
  Button,
  Divider,
  Menu,
  Modal,
  NumberInput,
  Popover,
  Select,
  Table,
  Text,
  TextInput,
} from "@mantine/core";

import {
  IconDots,
  IconEdit,
  IconExternalLink,
  IconFilter,
  IconPlus,
  IconReload,
  IconSelector,
  IconTrash,
} from "@tabler/icons-react";

import dayjs from "dayjs";

import DisplayAlert from "../../components/DisplayAlert";
import MegaLoader from "../../components/MegaLoader";
import TabularData from "../../components/TabularData";

import {
  AdminProfileRoles,
  Colors,
  CountriesWithDialingCode,
  SalesRequestStatuses,
} from "../../lib/Data";
import { Endpoints } from "../../lib/Endpoints";
import { NonPaginatedResponse } from "../../lib/Responses";
import { AdminProfileProps } from "../../lib/Types";
import { PerformRequest, usePerformRequest } from "../../lib/usePerformRequest";

import { useDisclosure } from "@mantine/hooks";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getColorFromRequestStatus,
  trimString,
  validateEmail,
} from "../../lib/Methods";
import "./styles.scss";
import { useForm } from "@mantine/form";
import { AppContext } from "../Dashboard";
import AnimateOnLoad from "../../components/AnimateOnLoad";
import DatagridTable from "../../components/DatagridTable";

const Team: React.FC = () => {
  const context = useContext(AppContext);

  const {
    data: team,
    isLoading: isTeamLoading,
    reloadData: reloadTeamMembers,
  } = usePerformRequest<NonPaginatedResponse<Omit<AdminProfileProps, "password">[]>>({
    method: "GET",
    route: Endpoints.GetTeamProfiles(),
  });

  const [currentActionsMenu, setCurrentActionsMenu] = useState<string>("");

  const [isUpdatingMembers, setUpdatingMembers] = useState<boolean>(false);
  const [
    isMemberFormModalOpen,
    { open: OpenMemberFormModal, close: CloseMemberFormModal },
  ] = useDisclosure(false);

  const [memberFormAction, setMemberFormAction] = useState<"update" | "add">("add");
  const memberForm = useForm({
    initialValues: {
      id: "",
      name: "",
      email: "",
      phone: "",
      role: "subadmin",
    },
    validate: {
      name: (value) => (value && value.length > 4 ? null : "Enter Name"),
      email: (value) => (validateEmail(value) ? null : "Invalid email"),
      role: (value) =>
        value && AdminProfileRoles.includes(value as any) ? null : "Select a role",
      phone: (value) => (value && trimString(value).length > 6 ? null : "Enter phone!"),
    },
  });

  const AddMemberProfile = async () => {
    const { hasErrors } = memberForm.validate();
    if (!hasErrors) {
      setUpdatingMembers(true);
      const { name, email, role, phone } = memberForm.values;
      const r = await PerformRequest({
        method: "POST",
        route: Endpoints.GetOnboardAdminProfile(),
        data: {
          name: trimString(name),
          email: trimString(email, true),
          phone: trimString(phone, true),
          role: trimString(role, true),
        },
        callback: () => {
          setUpdatingMembers(false);
        },
      });
      setUpdatingMembers(false);
      if (r) {
        if (r.status === 201) {
          DefaultSuccessNotification("Admin has been onboarded");
          memberForm.reset();
          CloseMemberFormModal();
          reloadTeamMembers();
        } else {
          DefaultErrorNotification("Email or Phone already taken!");
        }
      }
    }
  };
  const UpdateMemberProfile = async () => {
    const { hasErrors } = memberForm.validate();
    if (!hasErrors) {
      setUpdatingMembers(true);
      const { name, email, role, phone, id } = memberForm.values;
      const r = await PerformRequest({
        method: "PATCH",
        route: Endpoints.GetUpdateTeamMemberProfile(id),
        data: {
          name: trimString(name),
          email: trimString(email),
          phone: trimString(phone),
          role: trimString(role),
        },
        callback: () => {
          setUpdatingMembers(false);
        },
      });
      setUpdatingMembers(false);
      if (r) {
        if (r.status === 200) {
          DefaultSuccessNotification("Profile has been updated");
          memberForm.reset();
          CloseMemberFormModal();
          setMemberFormAction("add");
          reloadTeamMembers();
        }
        if (r.status === 409) {
          DefaultErrorNotification("Email or Phone is taken by other admin");
        }
      }
    }
  };
  const [memberToDelete, setMemberToDelete] =
    useState<Omit<AdminProfileProps, "password">>();
  const [
    isDeleteMemberModalOpen,
    { open: OpenDeleteMemberModal, close: CloseDeleteMemberModal },
  ] = useDisclosure(false);
  const DeleteMemberProfile = async (adminID: string) => {
    setUpdatingMembers(true);
    const r = await PerformRequest({
      method: "DELETE",
      route: Endpoints.GetDeleteAdminProfile(adminID),
      callback: () => {
        setUpdatingMembers(false);
      },
    });
    setUpdatingMembers(false);
    if (r) {
      if (r.status === 200) {
        DefaultErrorNotification("Member has been removed!");
        reloadTeamMembers();
        CloseDeleteMemberModal();
        setMemberToDelete(undefined);
      } else {
        DefaultErrorNotification("An error occurred!");
      }
    } else {
      DefaultErrorNotification("An error occurred!");
    }
  };
  const inputProps = {
    spellCheck: false,
    className: "input",
  };
  return (
    <AnimateOnLoad>
      <Container>
        {context && context.profile ? (
          <>
            <div className="sales-requests-container">
              <div className="flex-row align-center justify-between width-100">
                <Text className="component-header">Team Members</Text>
                <div className="flex-row align-center">
                  <ActionIcon
                    color={Colors.BluePrimary}
                    variant="outline"
                    disabled={isTeamLoading || isMemberFormModalOpen || isUpdatingMembers}
                    onClick={() => {
                      OpenMemberFormModal();
                      setMemberFormAction("add");
                    }}
                    size="lg"
                    mr={7}
                  >
                    <IconPlus size={16} />
                  </ActionIcon>
                  <Button
                    color={Colors.BluePrimary}
                    style={{ borderRadius: "50px" }}
                    loading={isTeamLoading}
                    onClick={reloadTeamMembers}
                    leftSection={<IconReload size={16} />}
                  >
                    Reload
                  </Button>
                </div>
              </div>
              <br />
              <br />

              <>
                {team && team.data && team.data.length > 0 ? (
                  <>
                    <DatagridTable
                      isDataLoading={isTeamLoading}
                      rows={team.data
                        .filter((m) => m.createdBy !== "OG")
                        .filter((m) => m.id !== context.profile?.id)
                        .map((member) => {
                          return member;
                        })}
                      columns={[
                        {
                          flex: 1,
                          field: "name",
                          headerName: "Name",
                        },
                        { flex: 1, field: "email", headerName: "Email" },
                        { flex: 1, field: "phone", headerName: "Phone" },
                        {
                          flex: 1,
                          field: "role",
                          headerName: "Role",
                          width: 120,
                        },
                        {
                          flex: 1,
                          field: "createdAt",
                          headerName: "Date Joined",
                          width: 110,
                          renderCell: (params) => {
                            return (
                              <span>
                                {dayjs(params.row.createdAt).format("ddd DD MMM YYYY")}
                              </span>
                            );
                          },
                        },

                        {
                          flex: 1,
                          field: "id",
                          headerName: "Actions",
                          renderCell: (params) => {
                            return (
                              <Menu
                                shadow="md"
                                opened={currentActionsMenu === params.row.id}
                                width={200}
                                onClose={() => {
                                  setCurrentActionsMenu("");
                                }}
                                trigger="hover"
                              >
                                <Menu.Target>
                                  <ActionIcon
                                    onClick={() => {
                                      setCurrentActionsMenu(
                                        currentActionsMenu === params.row.id
                                          ? ""
                                          : params.row.id
                                      );
                                    }}
                                    variant="subtle"
                                  >
                                    <IconDots size={15.5} />
                                  </ActionIcon>
                                </Menu.Target>

                                <Menu.Dropdown>
                                  <Menu.Label>Actions</Menu.Label>

                                  <Menu.Item
                                    leftSection={<IconTrash size={14} />}
                                    color={Colors.RedPrimary}
                                    disabled={context?.profile?.role !== "admin"}
                                    onClick={() => {
                                      OpenDeleteMemberModal();
                                      setMemberToDelete(params.row);
                                    }}
                                  >
                                    Delete
                                  </Menu.Item>
                                  <Menu.Item
                                    leftSection={<IconEdit size={14} />}
                                    disabled={context?.profile?.role !== "admin"}
                                    onClick={() => {
                                      memberForm.setValues({
                                        id: params.row.id,
                                        name: params.row.name,
                                        email: params.row.email,
                                        phone: params.row.phone,
                                        role: params.row.role,
                                      });
                                      setMemberFormAction("update");
                                      OpenMemberFormModal();
                                    }}
                                  >
                                    Edit Profile
                                  </Menu.Item>
                                </Menu.Dropdown>
                              </Menu>
                            );
                          },
                        },
                      ]}
                    />
                  </>
                ) : (
                  <>
                    {isTeamLoading ? (
                      <MegaLoader />
                    ) : (
                      <DisplayAlert
                        title="No Profiles Found"
                        children={
                          <Text>There are no Admin Accounts on the platform!</Text>
                        }
                      />
                    )}
                  </>
                )}
              </>
            </div>
            <Modal
              opened={isMemberFormModalOpen}
              onClose={CloseMemberFormModal}
              withCloseButton={false}
              centered
              size="lg"
            >
              <div className="add-member-modal flex-col">
                <Text className="header">Add New Member</Text>
                <Divider w="100%" my={10}></Divider>
                <div className="flex-row row align-center justify-between">
                  <TextInput
                    {...inputProps}
                    placeholder="Name"
                    label="Full Name"
                    {...memberForm.getInputProps("name")}
                  />
                  <Select
                    {...inputProps}
                    allowDeselect={false}
                    placeholder="Role"
                    label="Select Role"
                    data={AdminProfileRoles.map((r) => {
                      return { label: r, value: r };
                    })}
                    styles={{
                      label: { textTransform: "capitalize" },
                    }}
                    {...memberForm.getInputProps("role")}
                  />
                </div>
                <div className="flex-row row align-center justify-between">
                  <TextInput
                    {...inputProps}
                    placeholder="admin@email.com"
                    label="Email"
                    {...memberForm.getInputProps("email")}
                  />
                  <TextInput
                    type="tel"
                    {...inputProps}
                    leftSectionWidth={70}
                    leftSection={
                      <Autocomplete
                        clearable
                        placeholder="+1"
                        w={"100%"}
                        mr={20}
                        value={"+1"}
                        disabled
                        leftSectionWidth={20}
                        // leftSection={<IconSelector size={15} />}
                        // data={CountriesWithDialingCode.map((c) => {
                        //   return {
                        //     value: c.iso,
                        //     label: `+${c.code}`,
                        //   };
                        // })}
                      />
                    }
                    placeholder="Phone number"
                    label="Phone"
                    {...memberForm.getInputProps("phone")}
                  />
                </div>
                <div className="flex-row align-center justify-center width-100">
                  <Button
                    color={Colors.GreenPrimary}
                    className="btn"
                    onClick={
                      memberFormAction === "add" ? AddMemberProfile : UpdateMemberProfile
                    }
                    loading={isUpdatingMembers}
                    tt="capitalize"
                  >
                    {memberFormAction} Profile
                  </Button>
                </div>
              </div>
            </Modal>
            <Modal
              opened={isDeleteMemberModalOpen}
              onClose={CloseDeleteMemberModal}
              withCloseButton={false}
              centered
              size={300}
            >
              <div className="add-member-modal flex-col">
                <Text className="header">Delete Member Profile</Text>
                <Divider w="100%" my={10}></Divider>
                <Text>The following profile will be deleted:</Text>
                <div className="flex-row align-center">
                  <Text fw={500} w={60} fz={15} c={Colors.BluePrimary}>
                    Name
                  </Text>
                  <Text fw={400} fz={15}>
                    {memberToDelete?.name}
                  </Text>
                </div>
                <div className="flex-row align-center">
                  <Text fw={500} w={60} fz={15} c={Colors.BluePrimary}>
                    Email
                  </Text>
                  <Text fw={400} fz={15}>
                    {memberToDelete?.email}
                  </Text>
                </div>
                <div className="flex-row align-center">
                  <Text fw={500} w={60} fz={15} c={Colors.BluePrimary}>
                    Role
                  </Text>
                  <Text tt="capitalize" fw={400} fz={15}>
                    {memberToDelete?.role}
                  </Text>
                </div>
                <div className="flex-row align-center justify-center width-100">
                  <Button
                    color={Colors.RedPrimary}
                    className="btn"
                    onClick={() => {
                      DeleteMemberProfile(memberToDelete?.id as string);
                    }}
                    loading={isUpdatingMembers}
                  >
                    Confirm Delete
                  </Button>
                </div>
              </div>
            </Modal>
          </>
        ) : (
          <MegaLoader />
        )}
      </Container>
    </AnimateOnLoad>
  );
};
export default Team;
